<template>
  <div class="input-row--checkbox">
    <div
      class="form-input--checkbox"
      :class="{
        'form-input--checkbox--checked': isChecked,
        'form-input--checkbox--disabled': disabled,
      }"
    >
      <input
        v-show="false"
        :id="`${name}`"
        :name="`${name}`"
        type="checkbox"
        class="form-input--checkbox--input"
        :checked="isChecked ?? undefined"
        :disabled="disabled ?? undefined"
        :aria-disabled="disabled ?? undefined"
      />
      <div
        class="form-input--checkbox--box"
        data-pc-section="box"
        data-p="checked"
        role="presentation"
        @click="onClick"
        @keydown.space="onClick"
      >
        <svg
          v-if="isChecked"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="form-input--checkbox--icon"
          aria-hidden="true"
          data-pc-section="icon"
          data-p="checked"
        >
          <path
            d="M4.86199 11.5948C4.78717 11.5923 4.71366 11.5745 4.64596 11.5426C4.57826 11.5107 4.51779 11.4652 4.46827 11.4091L0.753985 7.69483C0.683167 7.64891 0.623706 7.58751 0.580092 7.51525C0.536478 7.44299 0.509851 7.36177 0.502221 7.27771C0.49459 7.19366 0.506156 7.10897 0.536046 7.03004C0.565935 6.95111 0.613367 6.88 0.674759 6.82208C0.736151 6.76416 0.8099 6.72095 0.890436 6.69571C0.970973 6.67046 1.05619 6.66385 1.13966 6.67635C1.22313 6.68886 1.30266 6.72017 1.37226 6.76792C1.44186 6.81567 1.4997 6.8786 1.54141 6.95197L4.86199 10.2503L12.6397 2.49483C12.7444 2.42694 12.8689 2.39617 12.9932 2.40745C13.1174 2.41873 13.2343 2.47141 13.3251 2.55705C13.4159 2.64268 13.4753 2.75632 13.4938 2.87973C13.5123 3.00315 13.4888 3.1292 13.4271 3.23768L5.2557 11.4091C5.20618 11.4652 5.14571 11.5107 5.07801 11.5426C5.01031 11.5745 4.9368 11.5923 4.86199 11.5948Z"
            fill="currentColor"
          ></path>
        </svg>
      </div>
    </div>

    <label v-if="label || $slots.label" :for="`${name}`" @click="onClick">
      <slot name="label">{{ label }}</slot>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue';

// Set a default of undefined because sometimes we intentionally do not
// bind a v-model. sometimes a checkbox is intended to be non-reactive.
const model = defineModel<boolean | undefined>({ default: undefined });
  
const props = defineProps<{
  checked?: boolean;
  label?: string;
  name?: string;
  disabled?: boolean;
}>();

const emit = defineEmits(['click']);

const isChecked = computed(() => {
  return model.value === true || props.checked === true;
});

const onClick = () => {
  if (props.disabled === true) {
    return;
  }

  if (model.value !== undefined) {
    model.value = !model.value;
  }

  nextTick(() => {
    emit('click', model.value);
  });
};
</script>

<style lang="scss">
.input-row--checkbox {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
  background: transparent;
  color: variables.$primitives-color-tertiary-500;
  position: relative;

  .form-input--checkbox {
    cursor: pointer;
    display: flex;
    height: variables.$sizing-md;
    position: relative;
    user-select: none;
    width: variables.$sizing-md;

    .form-input--checkbox--input {
      appearance: none;
      border: 1px solid transparent;
      border-radius: 6px;
      cursor: pointer;
      height: 100%;
      inset-block-start: 0;
      inset-inline-start: 0;
      margin: 0;
      opacity: 0;
      outline: 0 none;
      padding: 0;
      position: absolute;
      width: 100%;
      z-index: 1;
    }

    .form-input--checkbox--box {
      @include mixins.flex-center($horiz: center);
      @include mixins.rounded-border($color: variables.$primitives-color-primary-200, $size: 6px);
      background-color: variables.$primitives-color-background-100;
      color: variables.$primitives-color-background-100;

      height: variables.$sizing-md;
      outline-color: transparent;

      transition:
        background-color 0.2s ease-in-out,
        border-color 0.2s ease-in-out;
      width: variables.$sizing-md;
    }

    &.form-input--checkbox--disabled {
      .form-input--checkbox--box {
        background-color: variables.$primitives-color-background-200;
        border-color: variables.$primitives-color-border-200;
        cursor: not-allowed;
      }

      .form-input--checkbox--input {
        cursor: not-allowed;
      }
    }

    &.form-input--checkbox--checked {
      .form-input--checkbox--box {
        background-color: variables.$primitives-color-primary-200;
        border-color: variables.$primitives-color-primary-500;
      }
    }

    &.form-input--checkbox--checked.form-input--checkbox--disabled {
      .form-input--checkbox--box {
        opacity: 0.5;
      }
    }


    .form-input--checkbox--icon {
      height: 14px;
      width: 14px;
    }
  }

  label {
    @include mixins.flex-center($gap: variables.$sizing-xxxs);
  }
}
</style>
